/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';
import { isRightToLeft } from 'js/lib/language';

import { Button, Hidden, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import JoinForFreeButton from 'bundles/front-page/components/JoinForFreeButton';
import { getHeroModuleData } from 'bundles/front-page/constants/pathways';
import HeroModule from 'bundles/unified-common/modules/HeroModule';

import _t from 'i18n!nls/front-page';

const styles = {
  heading: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      text-align: center;
    }

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  imageOverlayLeft: css`
    position: absolute;

    ${breakpoints.down('sm')} {
      display: none;
    }

    ${breakpoints.up('md')} {
      left: 0;
      bottom: 80px;
      margin-right: 0;
    }
  `,
  imageOverlayRight: css`
    position: absolute;

    ${breakpoints.down('sm')} {
      display: none;
    }

    ${breakpoints.up('md')} {
      right: 0;
      bottom: 0;
      margin-right: 0;
    }
  `,
  imageOverlay: (borderColor?: string) => css`
    /**
     * Marketing design system token is not available in CDS, so hardcoding the value here
     * token: MDS/1 Core Blue/Blue 500
     */
    border: 2px solid ${borderColor ?? '#3587fc'};
    border-radius: var(--cds-border-radius-max);
    background: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-150) var(--cds-spacing-400);

    /** override CDS button styles */
    box-shadow: unset !important;

    .cds-button-label {
      display: flex;
      flex-direction: column;
    }

    &:hover,
    &:focus {
      text-decoration: none;
    }
  `,
  stats: (isRTL: boolean) => css`
    // Targeting spans to prevent them from being placed in random places in RTL mode
    span {
      display: ${isRTL ? 'inline-block' : 'inline'};
    }
  `,
};

function HeroModuleRenderer() {
  const track = useTracker();

  const { headline, description, heroImageUrlMobile, heroImageUrl, imageOverlayCopy, secondaryCta } =
    getHeroModuleData();

  const isRTL = isRightToLeft(_t.getLocale());
  const titleToShow = (titleCopy: string) => <div css={styles.heading}>{titleCopy}</div>;
  const descriptionToShow = (descriptionCopy: string) => <p css={styles.description}>{descriptionCopy}</p>;

  const trackClick = (linkName: string, linkTarget: string, roleSlug: string) => () => {
    track('click_button', {
      button: { name: 'role_info', linkName, linkURL: linkTarget, linkType: 'internal' },
      role: {
        roleSlug,
      },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const imageOverlay = imageOverlayCopy?.map((copy, index) => {
    let imageOverlayWrapperStyle = index === 0 ? styles.imageOverlayLeft : styles.imageOverlayRight;
    if (isRTL) {
      imageOverlayWrapperStyle = index === 0 ? styles.imageOverlayRight : styles.imageOverlayLeft;
    }
    const linkTarget = `/career-academy/roles/${copy.slug}`;
    return (
      <div key={copy.slug} css={imageOverlayWrapperStyle}>
        <Button
          css={styles.imageOverlay(copy.borderColor)}
          component="a"
          variant="secondary"
          href={linkTarget}
          onClick={trackClick('hero_module_role_link', linkTarget, copy.slug)}
        >
          <Typography2 variant="subtitleLarge" component="div">
            {copy.title}
          </Typography2>
          <Typography2 variant="bodySecondary" component="div" css={styles.stats(isRTL)}>
            <FormattedMessage
              message={_t('{medianSalary} median salary¹')}
              medianSalary={
                <FormattedNumber
                  value={copy.medianSalary}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="USD"
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                />
              }
            />
          </Typography2>
        </Button>
      </div>
    );
  });

  const getHeroProps = ({ isMobile }: { isMobile: boolean }) => ({
    headline: titleToShow(headline),
    description: descriptionToShow(description),
    image: {
      url: isMobile ? heroImageUrlMobile : heroImageUrl,
      width: isMobile ? 600 : 720,
    },
    layout: 'text6/image6',
    showImagesAboveContentOnMobile: false,
    imageOverlay: isMobile ? undefined : imageOverlay,
    customPrimaryCta: <JoinForFreeButton />,
    secondaryCtaText: secondaryCta.text,
    secondaryCtaTarget: secondaryCta.target,
    secondaryCtaVariant: isMobile ? 'ghost' : 'secondary',
    secondaryCtaEventingData: {
      linkType: 'internal',
    },
  });

  return (
    <React.Fragment>
      <Hidden smUp>
        <HeroModule {...getHeroProps({ isMobile: true })} />
      </Hidden>
      <Hidden xsDown>
        <HeroModule {...getHeroProps({ isMobile: false })} />
      </Hidden>
    </React.Fragment>
  );
}

export default HeroModuleRenderer;
